<script setup lang="ts">
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import TextInput from '../../TextInput.vue';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { ref, watch } from 'vue';
    import EnumToggleSwitch from '../../EnumToggleSwitch.vue';
    import IconButton from '../../IconButton.vue';
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import { CircleHelpIcon, SearchIcon } from '@lucide/vue';

    const filterStore = useFilterStoreByName(FilterStoreName.SharedFilter);

    const showSearchType = ref<boolean>(false);
    const showSearchTypeHelp = ref<boolean>(false);
    let mappedKeywordTypes: { value: string; label: string }[] | null = null;

    const includedKeyword = ref<string>('');

    const excludedKeyword = ref<string>('');

    const submitIncludedWord = () => {
        if (filterStore.all?.keywordFilter?.words && includedKeyword.value) {
            filterStore.setIncludedWords([
                ...(filterStore.all?.keywordFilter?.words || []),
                includedKeyword.value,
            ]);
            includedKeyword.value = '';
        }
    };

    const submitExcludedWord = () => {
        if (filterStore.all?.keywordFilter?.excludedWords && excludedKeyword.value) {
            filterStore.setExcludedWords([
                ...(filterStore.all?.keywordFilter?.excludedWords || []),
                excludedKeyword.value,
            ]);
            excludedKeyword.value = '';
        }
    };

    const removeIncludedWord = (word: string) => {
        filterStore.setIncludedWords(
            filterStore.all?.keywordFilter?.words.filter((w) => w !== word) || []
        );
    };

    const removeExcludedWord = (word: string) => {
        filterStore.setExcludedWords(
            filterStore.all?.keywordFilter?.excludedWords.filter((w) => w !== word) || []
        );
    };

    watch(
        () => filterStore.all?.keywordFilter,
        (newValue) => {
            if (newValue) {
                mappedKeywordTypes = newValue.availableWordSearchTypes.map((type) => {
                    return {
                        value: type,
                        label: __untypedExpression(type.toLocaleLowerCase()),
                    };
                });
            }
        },
        { immediate: true, deep: true }
    );
</script>
<template>
    <div class="w-full flex flex-col gap-3">
        <div class="w-full flex gap-3 items-center">
            <div class="w-1/2">
                <TextInput
                    v-model="includedKeyword"
                    class="w-full"
                    :placeholder="__('interactions-search-placeholder')"
                    :aria-label="__('interactions-search-placeholder')"
                    @keyup.enter="submitIncludedWord"
                    @blur="submitIncludedWord"
                    @focus="showSearchType = true"
                >
                    <template #end-adornment><SearchIcon /></template>
                </TextInput>
            </div>
            <div class="w-1/2">
                <button
                    v-for="word in filterStore.all?.keywordFilter?.words || []"
                    :key="word"
                    class="bg-gray-dark text-white cursor-pointer hover:bg-gray-medium px-2.5 py-0.5 rounded-full m-0.5"
                    :aria-label="__('remove-item', { word })"
                    @click="removeIncludedWord(word)"
                >
                    {{ word }}
                </button>
            </div>
        </div>
        <template v-if="showSearchType && filterStore.all?.keywordFilter">
            <div class="flex items-center px-3 py-1.5">
                <EnumToggleSwitch
                    id="keywordFilterSearchType"
                    v-model="filterStore.all.keywordFilter.selectedWordSearchType"
                    :items="mappedKeywordTypes || []"
                />
                <IconButton
                    :aria-label="__('help')"
                    size="size-6"
                    class="group rounded-full -my-2"
                    area-size="size-8"
                    @click="showSearchTypeHelp = !showSearchTypeHelp"
                >
                    <CircleHelpIcon class="size-5" />
                </IconButton>
                <p v-if="showSearchTypeHelp" class="Help--text max-w-120">
                    {{ __('keyword-tooltip') }}
                </p>
            </div>
        </template>
        <div class="w-full flex gap-3 items-center">
            <div class="w-1/2">
                <TextInput
                    v-model="excludedKeyword"
                    class="w-full"
                    :placeholder="__('interactions-excluded-search-placeholder')"
                    :aria-label="__('interactions-excluded-search-placeholder')"
                    @keyup.enter="submitExcludedWord"
                    @blur="submitExcludedWord"
                    @focus="showSearchType = true"
                />
            </div>
            <div class="w-1/2">
                <button
                    v-for="word in filterStore.all?.keywordFilter?.excludedWords || []"
                    :key="word"
                    class="bg-gray-light cursor-pointer hover:bg-gray-lightest px-2.5 py-0.5 rounded-full m-0.5"
                    :aria-label="__('remove-item', { word })"
                    @click="removeExcludedWord(word)"
                >
                    {{ word }}
                </button>
            </div>
        </div>
    </div>
</template>
